/**
 * outline.js — CodeMirror bindings for the engine's editor outline (rvmark/edit).
 */

import { foldService } from '@codemirror/language';
import { RangeSetBuilder, countColumn } from '@codemirror/state';
import { Decoration, ViewPlugin } from '@codemirror/view';
import { foldRanges, continueOutline, collectNodes } from 'rvmark/edit';

// Fold ranges by start line (1-based, as CodeMirror counts), per document.
const foldCache = new WeakMap();

function foldEnds(doc) {
  let ends = foldCache.get(doc);
  if (!ends) {
    ends = new Map();
    for (const [start, end] of foldRanges(doc.toString().split('\n'))) {
      ends.set(start + 1, Math.max(end + 1, ends.get(start + 1) ?? 0));
    }
    foldCache.set(doc, ends);
  }
  return ends;
}

export const rvmarkFolding = foldService.of((state, lineStart) => {
  const line = state.doc.lineAt(lineStart);
  const end = foldEnds(state.doc).get(line.number);
  return end ? { from: line.to, to: state.doc.line(end).to } : null;
});

export function continueOutlineCommand(view) {
  const { state } = view;
  const sel = state.selection;
  if (sel.ranges.length > 1 || !sel.main.empty) return false;
  const line = state.doc.lineAt(sel.main.head);
  const ins = continueOutline(state.doc.toString().split('\n'),
    { line: line.number - 1, col: sel.main.head - line.from });
  if (!ins) return false;
  const changes = state.changes({ from: state.doc.line(ins.at.line + 1).from + ins.at.col, insert: ins.text });
  const doc = changes.apply(state.doc);
  view.dispatch({
    changes,
    selection: { anchor: doc.line(ins.cursor.line + 1).from + ins.cursor.col },
    scrollIntoView: true,
    userEvent: 'input',
  });
  return true;
}

// Wrapped rows hang under the node's text; every other line under its own
// indent. The line carries only the width: the theme owns .cm-line's padding
// and adds --hang to it.
const hang = new Map();
function hangDeco(cols) {
  let deco = hang.get(cols);
  if (!deco) {
    deco = Decoration.line({ attributes: { style: `--hang: ${cols}ch` } });
    hang.set(cols, deco);
  }
  return deco;
}

// Outline nodes by line (1-based), per document.
const nodeCache = new WeakMap();

function nodeLines(doc) {
  let nodes = nodeCache.get(doc);
  if (!nodes) {
    nodes = new Map(collectNodes(doc.toString().split('\n')).map(n => [n.lineIndex + 1, n]));
    nodeCache.set(doc, nodes);
  }
  return nodes;
}

// Indent, marker, and the blanks after it. Other whitespace the parser accepts
// there is left to the text: its width is not a column count.
const PREFIX = /^[ \t]*(?:[a-zA-Z0-9]+\.|[-*])?[ \t]*/;
const INDENT = /^[ \t]*/;

function wrapIndents(view) {
  const { doc, tabSize } = view.state;
  const nodes = nodeLines(doc);
  const builder = new RangeSetBuilder();
  for (const { from, to } of view.visibleRanges) {
    for (let pos = from; pos <= to;) {
      const line = doc.lineAt(pos);
      const prefix = (nodes.has(line.number) ? PREFIX : INDENT).exec(line.text)[0];
      if (prefix) builder.add(line.from, line.from, hangDeco(countColumn(prefix, tabSize)));
      pos = line.to + 1;
    }
  }
  return builder.finish();
}

export const rvmarkWrapIndent = ViewPlugin.fromClass(class {
  constructor(view) { this.decorations = wrapIndents(view); }
  update(u) { if (u.docChanged || u.viewportChanged) this.decorations = wrapIndents(u.view); }
}, { decorations: (v) => v.decorations });
